import { ChangeEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import Select, { SingleValue } from "react-select";

import { ReactComponent as CubeGreenIcon } from "../../assets/Cube_green.svg";
import { ReactComponent as CubeGrayIcon } from "../../assets/Cube_grey.svg";

import LoadingState from "../../components/LoadingState";

import Municipalities from "../../i18n/municipalities";
import Grades from "../../i18n/grades";

import useParticipants from "./hook/useParticipants";

import type { Participant, Filter } from "./types/participant";

function App() {
  const locale = (WP_VARS.locale || "sv_SE") as "sv_SE" | "fi" | "dk";
  const { t } = useTranslation();

  const [filter, setFilter] = useState<Filter>({
    municipality: "",
    grade: "",
    school: "",
  } as Filter);

  const {
    participants,
    setParticipants,
    loading,
    hasMorePosts,
    errorMessage,
    loadMore,
    setOffset,
  } = useParticipants(filter);

  const handleMunicipalityChange = (
    option: SingleValue<{
      value: string;
      label: string;
    }>
  ) => {
    setFilter({ ...filter, municipality: option?.value || "" });
    setParticipants([]);
    setOffset(0);
  };

  const handleGradeChange = (
    option: SingleValue<{
      value: string;
      label: string;
    }>
  ) => {
    setFilter({ ...filter, grade: option?.value || "" });
    setParticipants([]);
    setOffset(0);
  };

  const handleSchoolChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setFilter({ ...filter, school: value });
    setParticipants([]);
    setOffset(0);
  };

  const handleTableRowClick = (path: string) => {
    window.location.href = path;
  };

  return (
    <div className="joy-participants">
      <div className="joy-input-row">
        <div className={"joy-select joy-25"}>
          <label htmlFor="participants-municipality">
            {t("participants.municipality")}
          </label>
          <Select<{ value: string; label: string }>
            options={[
              { value: "", label: t("participants.all_options") as string },
              ...(Municipalities?.[locale]?.map((municipality: string) => ({
                value: municipality,
                label: municipality,
              })) || []),
            ]}
            defaultValue={{ value: "", label: t("participants.all_options") }}
            onChange={handleMunicipalityChange}
            id="participants-municipality"
          />
        </div>
        <div className={"joy-select joy-25"}>
          <label htmlFor="participants-grade">{t("participants.grade")}</label>
          <Select<{ value: string; label: string }>
            options={[
              { value: "", label: t("participants.all_options") as string },
              ...(Grades?.[locale]?.map(({ value, label }) => ({
                value: value,
                label: label,
              })) || []),
            ]}
            defaultValue={{ value: "", label: t("participants.all_options") }}
            onChange={handleGradeChange}
            id="participants-grade"
          />
        </div>
        <div className={"joy-input joy-50"}>
          <label htmlFor="participants-text">{t("participants.school")}</label>
          <input
            id="participants-text"
            type="text"
            onChange={handleSchoolChange}
          />
        </div>
      </div>
      <div className="joy-table">
        {participants.length > 0 && (
          <table>
            <thead>
              <tr>
                <th>{t("participants.school")}</th>
                <th>{t("participants.class")}</th>
                <th>
                  <span className="joy-table-header-match-1">
                    {t("participants.match1")}
                  </span>
                </th>
                <th>
                  <span className="joy-table-header-match-2">
                    {t("participants.match2")}
                  </span>
                </th>
                <th>
                  <span className="joy-table-header-match-1c">
                    {t("participants.match3")}
                  </span>
                </th>
                <th>
                  <span className="joy-table-header-match-1b">
                    {t("participants.match4")}
                  </span>
                </th>
                <th>
                  <span className="joy-table-header-match-3">
                    {t("participants.match5")}
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              {participants.map((item: Participant, index: number) => (
                <tr key={index} onClick={() => handleTableRowClick(item.link)}>
                  <td>
                    <span className="joy-table-data-school">{item.school}</span>
                  </td>
                  <td>
                    <span className="joy-table-data-class">{item.class}</span>
                  </td>
                  <td>
                    <span className="joy-table-data-match-1">
                      {item.match1 ? <CubeGreenIcon /> : <CubeGrayIcon />}
                    </span>
                  </td>
                  <td>
                    <span className="joy-table-data-match-2">
                      {item.match2 ? <CubeGreenIcon /> : <CubeGrayIcon />}
                    </span>
                  </td>
                  <td>
                    <span className="joy-table-data-match-1c">
                      {item.match1c ? <CubeGreenIcon /> : <CubeGrayIcon />}
                    </span>
                  </td>
                  <td>
                    <span className="joy-table-data-match-1b">
                      {item.match1b ? <CubeGreenIcon /> : <CubeGrayIcon />}
                    </span>
                  </td>
                  <td>
                    <span className="joy-table-data-match-3">
                      {item.match3 ? <CubeGreenIcon /> : <CubeGrayIcon />}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {!!errorMessage && (
        <p className="joy-error-message">{t(`errors.${errorMessage}`)}</p>
      )}
      {!!loading && <LoadingState />}
      <div className={"joy-participants-actions"}>
        {hasMorePosts && (
          <button
            disabled={!hasMorePosts}
            onClick={() => loadMore()}
            className={"joy-button"}
          >
            {t("participants.show_more")}
          </button>
        )}
      </div>
    </div>
  );
}

export default App;
